/**
 * Accept Invite Page
 *
 * Handles accepting team invitations via magic link tokens.
 *
 * @module pages/AcceptInvitePage
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { useAcceptInvitation } from '@/hooks/useMembers';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui';
import { Spinner, Button, Card } from '@/components/ui';
import { CheckCircle, XCircle } from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

interface InvitationDetails {
  valid: boolean;
  email?: string;
  organizationName?: string;
  inviterName?: string;
  role?: string;
  expired?: boolean;
  alreadyAccepted?: boolean;
  userExists?: boolean;
}

// =============================================================================
// Component
// =============================================================================

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const { firebaseUser, loading: authLoading, switchOrganization, refreshAuth } = useAuth();
  const { acceptInvitation, loading: acceptLoading } = useAcceptInvitation();
  const toast = useToast();

  const [status, setStatus] = useState<'loading' | 'pending' | 'success' | 'error'>('loading');
  const [inviteDetails, setInviteDetails] = useState<InvitationDetails | null>(null);
  const [organizationName, setOrganizationName] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fetch invitation details on mount (no auth required)
  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Invalid invitation link. No token provided.');
      return;
    }

    const fetchDetails = async () => {
      try {
        const getInvitationDetails = httpsCallable<{ token: string }, InvitationDetails>(
          functions,
          'getInvitationDetails'
        );
        const result = await getInvitationDetails({ token });
        setInviteDetails(result.data);

        if (!result.data.valid) {
          setStatus('error');
          if (result.data.expired) {
            setErrorMessage('This invitation has expired. Please ask for a new invitation.');
          } else if (result.data.alreadyAccepted) {
            setErrorMessage('This invitation has already been accepted.');
          } else {
            setErrorMessage('Invalid invitation link.');
          }
        } else {
          setStatus('pending');
        }
      } catch (err) {
        console.error('Failed to fetch invitation details:', err);
        setStatus('error');
        setErrorMessage('Failed to load invitation details. Please try again.');
      }
    };

    fetchDetails();
  }, [token]);

  // Handle auth state changes and redirect or accept
  useEffect(() => {
    // Still loading invitation details
    if (status === 'loading') return;

    // Invalid invitation, show error
    if (status === 'error') return;

    // Wait for auth to load
    if (authLoading) return;

    // If not logged in, redirect to login or signup based on whether user exists
    if (!firebaseUser && inviteDetails?.valid) {
      const destination = inviteDetails.userExists ? '/login' : '/signup';
      navigate(destination, {
        state: {
          from: { pathname: '/accept-invite', search: `?token=${token}` },
          isInvite: true,
          inviteDetails: {
            email: inviteDetails.email,
            organizationName: inviteDetails.organizationName,
            inviterName: inviteDetails.inviterName,
            role: inviteDetails.role,
          },
        },
      });
      return;
    }

    // User is logged in, accept the invitation
    if (firebaseUser && inviteDetails?.valid && status === 'pending') {
      const accept = async () => {
        try {
          const result = await acceptInvitation(token!);
          setOrganizationName(result.organizationName);
          setStatus('success');
          toast.success(`You've joined ${result.organizationName}!`);

          // Refresh auth state to pick up server-side email verification
          await refreshAuth();

          // Switch to the newly joined organization
          await switchOrganization(result.organizationId);

          // Redirect after a short delay - let ProtectedRoute handle routing
          // to appropriate onboarding or dashboard
          setTimeout(() => {
            navigate('/');
          }, 2000);
        } catch (err) {
          setStatus('error');
          const message = err instanceof Error ? err.message : 'Failed to accept invitation.';
          const cleanMessage = message
            .replace(/^Firebase:\s*/, '')
            .replace(/\s*\(.*\)\s*$/, '')
            .trim();
          setErrorMessage(cleanMessage || 'Failed to accept invitation.');
        }
      };

      accept();
    }
  }, [status, authLoading, firebaseUser, inviteDetails, token, acceptInvitation, switchOrganization, refreshAuth, navigate, toast]);

  // Show loading while fetching invitation details or auth
  if (status === 'loading' || (status === 'pending' && (authLoading || acceptLoading))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full mx-4 text-center">
          <div className="py-8">
            <Spinner size="lg" className="mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              {status === 'loading' ? 'Loading Invitation' : 'Accepting Invitation'}
            </h1>
            <p className="text-gray-500">
              {status === 'loading'
                ? 'Please wait while we load your invitation...'
                : 'Please wait while we process your invitation...'}
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Show success state
  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full mx-4 text-center">
          <div className="py-8">
            <div className="mx-auto w-16 h-16 bg-success-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-success-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Welcome to {organizationName}!
            </h1>
            <p className="text-gray-500 mb-6">
              You've successfully joined the organization. Redirecting you now...
            </p>
            <Button onClick={() => navigate('/')}>
              Continue
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Show error state
  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="max-w-md w-full mx-4 text-center">
          <div className="py-8">
            <div className="mx-auto w-16 h-16 bg-error-100 rounded-full flex items-center justify-center mb-4">
              <XCircle className="w-8 h-8 text-error-600" />
            </div>
            <h1 className="text-xl font-semibold text-gray-900 mb-2">
              Unable to Accept Invitation
            </h1>
            <p className="text-gray-500 mb-6">
              {errorMessage || 'The invitation link may be invalid or expired.'}
            </p>
            <div className="space-y-3">
              <Button onClick={() => navigate('/overview')} className="w-full">
                Go to Dashboard
              </Button>
              <Link
                to="/login"
                className="text-sm text-brand-600 hover:text-brand-700"
              >
                Try with a different account
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // Fallback loading state
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Spinner size="lg" />
    </div>
  );
}
